import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Send, Heart, Users, FileText, MessageCircle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
// Using browser's native alert for notifications

export const ContactPage: React.FC = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  useEffect(() => {
    const state = location.state as any;
    const target = state?.scrollTo;
    if (target) {
      const el = document.getElementById(target);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    if (state?.camp) {
      const campName = state.camp as string;
      setFormData((prev) => ({
        ...prev,
        subject: prev.subject || 'blood-drive',
        message:
          prev.message ||
          `I would like to join the '${campName}' blood donation camp. Please share the registration process and available time slots.`,
      }));
    }
  }, [location.state]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const to = 'support@navjeevanbloodcentre.org';
      const subject = encodeURIComponent(`Contact: ${formData.subject || 'General Inquiry'}`);
      const bodyLines = [
        `Name: ${formData.name}`,
        `Email: ${formData.email}`,
        formData.phone ? `Phone: ${formData.phone}` : '',
        '',
        'Message:',
        formData.message,
      ].filter(Boolean);
      const body = encodeURIComponent(bodyLines.join('\n'));

      window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;

      alert('Your email client will open to send the message.');

      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error preparing email:', error);
      alert('Could not open your email client. Please email support@navjeevanbloodcentre.org directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Visit Our Center',
      details: [
        'D. No. 18-77/78, 1st floor, Rudra Complex,',
        'Street No. 3, V.V. Nagar Road, Kamala Nagar,',
        'Opp. Manepally Jewellers,',
        'Metro Pillar No. 1556, Chaitanyapuri,',
        'Dilsukhnagar, Hyderabad-60.'
      ],
      action: 'Get Directions'
    },
    {
      icon: Phone,
      title: 'Call Us',
      details: [
        'Phone: 6302122760',
        'Phone: 9032187760'
      ],
      action: 'Call Now'
    },
    {
      icon: Mail,
      title: 'Email Us',
      details: [
        'support@navjeevanbloodcentre.org'
      ],
      action: 'Send Email'
    }
  ];

  const operatingHours = [
    { day: 'Monday - Saturday', hours: '10:00 AM - 6:00 PM' },
    { day: 'Sunday', hours: 'Closed (Sunday holiday)' },
    { day: 'Public Holidays', hours: 'Please call for emergency needs' }
  ];

  const quickLinks = [
    {
      icon: Heart,
      title: 'Donor Registration',
      description: 'Ready to donate? Register online to schedule your appointment.',
      link: '/donors'
    },
    {
      icon: FileText,
      title: 'Eligibility Check',
      description: 'Review our eligibility requirements before your visit.',
      link: '/donors#eligibility'
    },
    {
      icon: Users,
      title: 'Host a Blood Drive',
      description: 'Organize a blood donation camp in your community.',
      link: '/camps'
    },
    {
      icon: MessageCircle,
      title: 'FAQ',
      description: 'Find answers to common questions about blood donation.',
      link: '/blood-groups#faq'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Page Hero */}
      <section className="py-16 bg-primary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-title font-bold text-neutral-900 mb-4">Get in Touch</h1>
          <p className="text-body-lg text-neutral-600 max-w-3xl mx-auto">
            We're here to help with any questions about blood donation, organize a blood drive, 
            or provide support for your blood banking needs.
          </p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-subtitle font-bold text-neutral-900 mb-4">Contact Information</h2>
            <p className="text-body-lg text-neutral-600">
              Multiple ways to reach us for your convenience
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {contactInfo.map((info, index) => {
              const IconComponent = info.icon;
              return (
                <Card key={index} hover className="text-center">
                  <CardContent>
                    <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <IconComponent className="h-8 w-8 text-primary-600" />
                    </div>
                    <CardTitle>{info.title}</CardTitle>
                    <div className="space-y-2 mt-4 mb-6">
                      {info.details.map((detail, detailIndex) => (
                        <p key={detailIndex} className="text-body text-neutral-700">{detail}</p>
                      ))}
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (info.title === 'Email Us') {
                          window.location.href = 'mailto:support@navjeevanbloodcentre.org';
                        }
                      }}
                    >
                      {info.action}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Operating Hours */}
      <section className="py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-8 w-8 text-primary-600" />
                </div>
                <CardTitle>Operating Hours</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {operatingHours.map((schedule, index) => (
                    <div key={index} className="flex justify-between items-center py-3 border-b border-neutral-200 last:border-b-0">
                      <span className="text-body font-medium text-neutral-900">{schedule.day}</span>
                      <span className="text-body text-neutral-600">{schedule.hours}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-primary-50 rounded-lg">
                  <p className="text-body text-neutral-700 text-center">
                    <strong>Note:</strong> Hours may vary during holidays and special events. 
                    Please call ahead for current operating hours or emergency blood needs.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-24 bg-white" id="contact-form">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-subtitle font-bold text-neutral-900 mb-4">Send Us a Message</h2>
            <p className="text-body-lg text-neutral-600">
              Have a question or need assistance? Fill out the form below and we'll get back to you within 24 hours.
            </p>
          </div>
          <Card>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-small font-medium text-neutral-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-small font-medium text-neutral-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter your email address"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-small font-medium text-neutral-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="(+91) 123-4567"
                    />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-small font-medium text-neutral-700 mb-2">
                      Subject *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Select a subject</option>
                      <option value="general">General Inquiry</option>
                      <option value="donation">Donation Questions</option>
                      <option value="eligibility">Eligibility Requirements</option>
                      <option value="blood-drive">Organize Blood Drive</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="message" className="block text-small font-medium text-neutral-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-vertical"
                    placeholder="Please provide details about your inquiry..."
                  ></textarea>
                </div>
                <div className="text-center">
                  <Button 
                    type="submit" 
                    size="lg"
                    disabled={isSubmitting}
                    className="min-w-[180px]"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-5 w-5" />
                        Send Message
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      
      {/* Call to Action */}
      <section className="py-24 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-title font-bold mb-6">Ready to Make a Difference?</h2>
          <p className="text-body-lg mb-8 text-red-100">
            Join thousands of donors who are making a life-saving impact in our community. 
            Your next donation could be the one that saves a life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/donors" state={{ scrollTo: 'register-to-donate' }}>
              <Button size="lg" className="bg-white text-primary-600 hover:bg-gray-100">
                Register as Donor
              </Button>
            </Link>
            <Link to="/contact" state={{ scrollTo: 'contact-form' }}>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-primary-600">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-subtitle font-bold text-neutral-900 mb-4">Quick Links</h2>
            <p className="text-body-lg text-neutral-600">
              Find what you need quickly with these helpful resources
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {quickLinks.map((link, index) => {
              const IconComponent = link.icon;
              return (
                <Card key={index} hover className="text-center cursor-pointer">
                  <CardContent>
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="h-6 w-6 text-primary-600" />
                    </div>
                    <h3 className="text-body font-semibold text-neutral-900 mb-2">{link.title}</h3>
                    <p className="text-small text-neutral-600">{link.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

    </div>
  );
};
